// Welke toon-als-regels breken als je deze vragen op een meetmoment uitzet?
//
// Twee zichtbaarheidsmechanismen raken elkaar hier. Zet je de triggervraag op
// een meetmoment uit, dan kan de regel niet meer beoordeeld worden. Het
// leespad faalt dan bewust OPEN: de vervolgvraag wordt gewoon getoond — een
// vraag stil laten verdwijnen is de ergere fout. Maar dat betekent wel dat de
// bouwer iets anders krijgt dan hij denkt: een vraag die hij als
// 'alleen soms' instelde, wordt op dit meetmoment aan iedereen gesteld.
//
// Daarom deze melding. De regel wordt niet stiekem gerepareerd of geblokkeerd —
// de bouwer krijgt te zien wat de keuze kost.
//
// AI-CONTEXT: See docs/features/conditional-questions.md

import isStandardQuestion from './isStandardQuestion'

/**
 * @typedef {object} LostTrigger
 * @property {{ID: string, Question: string}} dependent - De vraag die zijn regel verliest
 * @property {{ID: string, Question: string}} trigger - De uitgezette vraag waar hij van afhing
 */

/**
 * @param {Array<object>} fields - Alle QuestionnaireFields van de vragenlijst
 * @param {Array<string>} excludedFieldIDs - Wat er op dit meetmoment uitstaat
 *   (vraag-ID's én sectie-ID's, net als `MeasureMoments.ExcludedFieldIDs`)
 * @returns {Array<LostTrigger>}
 */
const triggersLostByExclusion = (fields, excludedFieldIDs) => {
    if (!Array.isArray(fields)) return []

    const excluded = new Set(excludedFieldIDs || [])
    if (excluded.size === 0) return []

    // Zelfde regel als filterFieldsForMoment: een uitgezette SECTIE zet zijn
    // vragen mee uit, en een meetstandaardvraag is nooit uit te zetten.
    const isOff = (field) => (
        !isStandardQuestion(field)
        && (excluded.has(field?.ID) || excluded.has(field?.SectionID))
    )

    const byId = new Map(fields.filter((field) => field?.ID).map((field) => [field.ID, field]))

    return fields
        .filter((field) => field?.ShowWhen?.FieldID && !isOff(field))
        .map((field) => ({ field, trigger: byId.get(field.ShowWhen.FieldID) }))
        // Een regel naar een niet-bestaande vraag is een ander probleem; daar
        // waarschuwt de chip in de bouwer voor.
        .filter(({ trigger }) => trigger && isOff(trigger))
        .map(({ field, trigger }) => ({
            dependent: { ID: field.ID, Question: field.Question },
            trigger: { ID: trigger.ID, Question: trigger.Question },
        }))
}

export default triggersLostByExclusion
